/**
 * Admin: Cache Layer.
 *
 * UX for the static blob-storage cache (see cache.md):
 *
 *   - POST /api/rebuildCache  → queue a cache-rebuild job (or run inline
 *                                with `quick=true&dryRun=true`).
 *   - GET  /api/rebuildCache  → latest meta/manifest.json (per-section
 *                                durations, counts, errors).
 *   - GET  /api/getAppMetadata → meta/app.json snapshot consumed by app
 *                                landing pages and dashboards.
 *
 * The page lets an admin:
 *   1. Trigger a rebuild (dry run / quick / full),
 *   2. View the latest manifest (and refresh it while a queued job runs),
 *   3. Inspect the prebuilt app metadata snapshot.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw,
  PlayCircle,
  FileSearch,
  Info,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  BarChart3,
} from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { useAuth } from '../contexts/AuthContext';
import {
  triggerCacheRebuild,
  getCacheManifest,
  getAppMetadata,
  getCacheStats,
  saveLastRun,
  loadLastRun,
  type CacheManifest,
  type CacheStatsResponse,
  type CacheStatsGate,
  type RebuildCacheTriggerResponse,
  type LastRunRecord,
} from '../services/adminApi';
import type { AppMetadata } from '../types/api';
import {
  Card,
  CardTitle,
  CardDescription,
  Banner,
  PrimaryButton,
  SecondaryButton,
  RunningIndicator,
  StatGrid,
  Metric,
  RawJson,
  LastRunCard,
  SampleTable,
  ButtonRow,
  Field,
  Label,
  Input,
  Select,
  InlineLabel,
} from '../components/admin/AdminUI';
import { colors, spacing, typography } from '../styles/theme';
import { formatEventLocalDateTime } from '../utils/eventDateTime';

const ACTION = 'rebuildCache';

function fmtDuration(ms?: number): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  const rem = Math.floor(s % 60);
  return `${m}m ${rem}s`;
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function relativeTime(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return iso;
  const delta = Date.now() - t;
  if (delta < 60_000) return 'just now';
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return new Date(iso).toLocaleString();
}

const SECTION_LABELS: Record<string, string> = {
  cities: 'Cities',
  categoriesVirtual: 'Virtual categories',
  partners: 'Tech Companies',
  companies: 'Corporations',
  appMetadata: 'App metadata',
};

export const AdminCachePage: React.FC = () => {
  const { userInfo } = useAuth();

  // Trigger options
  const [dryRun, setDryRun] = useState(false);
  const [quick, setQuick] = useState(false);

  // Trigger state
  const [triggering, setTriggering] = useState(false);
  const [triggeredAt, setTriggeredAt] = useState<number | null>(null);
  const [trigger, setTrigger] = useState<RebuildCacheTriggerResponse | null>(null);
  const [triggerError, setTriggerError] = useState<string | null>(null);

  // Manifest state
  const [manifest, setManifest] = useState<CacheManifest | null>(null);
  const [manifestLoading, setManifestLoading] = useState(false);
  const [manifestError, setManifestError] = useState<string | null>(null);
  const [manifestMissing, setManifestMissing] = useState(false);
  const [manifestRefreshedAt, setManifestRefreshedAt] = useState<Date | null>(null);

  // App metadata state
  const [appMeta, setAppMeta] = useState<AppMetadata | null>(null);
  const [appMetaLoading, setAppMetaLoading] = useState(false);
  const [appMetaError, setAppMetaError] = useState<string | null>(null);
  const [appMetaMissing, setAppMetaMissing] = useState(false);
  const [appMetaRefreshedAt, setAppMetaRefreshedAt] = useState<Date | null>(null);

  const [lastRun, setLastRun] = useState<
    LastRunRecord<RebuildCacheTriggerResponse> | null
  >(null);

  // Cache stats state
  const [statsSince, setStatsSince] = useState<string>('P1D');
  const [statsGate, setStatsGate] = useState<'' | CacheStatsGate>('');
  const [statsGroupBy, setStatsGroupBy] = useState<string>('');
  const [statsTop, setStatsTop] = useState<string>('');
  const [statsRaw, setStatsRaw] = useState<boolean>(false);
  const [stats, setStats] = useState<CacheStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsMissing, setStatsMissing] = useState(false);
  const [statsRefreshedAt, setStatsRefreshedAt] = useState<Date | null>(null);

  useEffect(() => { setLastRun(loadLastRun(ACTION)); }, []);

  // ---------------------------------------------------------------------
  // Loaders
  // ---------------------------------------------------------------------

  const loadManifest = useCallback(async () => {
    setManifestLoading(true);
    setManifestError(null);
    try {
      const data = await getCacheManifest();
      if (data === null) {
        setManifest(null);
        setManifestMissing(true);
      } else {
        setManifest(data);
        setManifestMissing(false);
      }
      setManifestRefreshedAt(new Date());
    } catch (e: any) {
      setManifestError(e?.message ?? 'Failed to load manifest');
    } finally {
      setManifestLoading(false);
    }
  }, []);

  const loadAppMeta = useCallback(async () => {
    setAppMetaLoading(true);
    setAppMetaError(null);
    try {
      const data = await getAppMetadata();
      if (data === null) {
        setAppMeta(null);
        setAppMetaMissing(true);
      } else {
        setAppMeta(data);
        setAppMetaMissing(false);
      }
      setAppMetaRefreshedAt(new Date());
    } catch (e: any) {
      setAppMetaError(e?.message ?? 'Failed to load app metadata');
    } finally {
      setAppMetaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManifest();
    loadAppMeta();
  }, [loadManifest, loadAppMeta]);

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const groupBy = statsGroupBy
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const topNum = statsTop.trim() ? Number(statsTop) : undefined;
      const data = await getCacheStats({
        since: statsSince.trim() || undefined,
        gate: statsGate || undefined,
        groupBy: groupBy.length ? groupBy : undefined,
        top: Number.isFinite(topNum) ? topNum : undefined,
        raw: statsRaw,
      });
      if (data === null) {
        setStats(null);
        setStatsMissing(true);
      } else {
        setStats(data);
        setStatsMissing(false);
      }
      setStatsRefreshedAt(new Date());
    } catch (e: any) {
      setStatsError(e?.message ?? 'Failed to load cache stats');
    } finally {
      setStatsLoading(false);
    }
  }, [statsSince, statsGate, statsGroupBy, statsTop, statsRaw]);

  // Auto-load stats once on mount with the defaults.
  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------------
  // Trigger rebuild
  // ---------------------------------------------------------------------

  const doTrigger = async () => {
    setTriggering(true);
    setTriggerError(null);
    setTrigger(null);
    setTriggeredAt(Date.now());
    try {
      const { data, status } = await triggerCacheRebuild({ dryRun, quick });
      setTrigger(data);

      const record: LastRunRecord<RebuildCacheTriggerResponse> = {
        timestamp: new Date().toISOString(),
        triggeredBy: userInfo.userName || userInfo.userId,
        summary: {
          httpStatus: status,
          mode: data.mode ?? (status === 202 ? 'queued' : 'inline'),
          dryRun: dryRun,
          quick: quick,
          jobId: data.jobId ?? '—',
        },
        response: data,
      };
      saveLastRun(ACTION, record);
      setLastRun(record);

      // Refresh manifest right away so the page shows the new (or in-progress)
      // build state.
      await loadManifest();
      // Quick + dryRun runs may also rewrite the app metadata blob.
      if (quick) await loadAppMeta();
    } catch (e: any) {
      setTriggerError(e?.message ?? 'Trigger failed');
    } finally {
      setTriggering(false);
      setTriggeredAt(null);
    }
  };

  // While a queued job is in flight, the manifest endpoint will still return
  // the previous build until the queue trigger writes the new one. Allow the
  // admin to refresh manually, and auto-refresh every 10 s while a queued
  // trigger is "active" (until the manifest's startedAt advances).
  const queuedJobAcked =
    !!trigger?.jobId && (trigger.mode ?? 'queued') === 'queued';

  useEffect(() => {
    if (!queuedJobAcked) return;
    const interval = window.setInterval(() => {
      loadManifest();
    }, 10_000);
    return () => window.clearInterval(interval);
  }, [queuedJobAcked, loadManifest]);

  // ---------------------------------------------------------------------
  // Derived display values
  // ---------------------------------------------------------------------

  const sectionRows = useMemo(() => {
    if (!manifest?.sections) return [];
    return Object.entries(manifest.sections).map(([key, section]) => ({
      key,
      label: SECTION_LABELS[key] ?? key,
      count: section?.count,
      durationMs: section?.durationMs,
      skipped: section?.skipped,
      errorCount: Array.isArray(section?.errors) ? section!.errors!.length : 0,
    }));
  }, [manifest]);

  const totalManifestErrors = useMemo(() => {
    if (!manifest) return 0;
    let n = Array.isArray(manifest.errors) ? manifest.errors.length : 0;
    if (manifest.sections) {
      for (const s of Object.values(manifest.sections)) {
        if (Array.isArray(s?.errors)) n += s!.errors!.length;
      }
    }
    return n;
  }, [manifest]);

  const inlineManifest = trigger?.manifest;

  return (
    <AdminLayout
      currentPage="cache"
      pageTitle="Cache Layer"
      pageDescription="Pre-computed search results and app metadata stored in Azure Blob Storage. Trigger rebuilds, inspect the latest build manifest, and view the app metadata snapshot."
    >
      <Banner tone="info">
        <strong>What this page controls.</strong> The cache pre-computes the
        most popular search results (top cities, virtual categories, top
        tech companies and corporations) plus an app-wide metadata snapshot. A full
        rebuild can take several minutes, so the trigger enqueues a background
        job — this page polls the manifest until it updates. See{' '}
        <code>cache.md</code> for the cache-gate criteria.
      </Banner>

      {/* ----------------------------- Trigger card ------------------------ */}
      <Card>
        <CardTitle>
          <PlayCircle size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Trigger rebuild
        </CardTitle>
        <CardDescription>
          By default this enqueues a background job and returns immediately
          with a <code>jobId</code>. Use <strong>Dry run</strong> to exercise
          the build pipeline without writing any blobs. <strong>Quick</strong>{' '}
          skips the queue and runs inline — only safe combined with dry run,
          since a real build will not finish inside the HTTP timeout.
        </CardDescription>

        <Field>
          <InlineLabel>
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              disabled={triggering}
            />
            Dry run (don't write blobs)
          </InlineLabel>
          <InlineLabel>
            <input
              type="checkbox"
              checked={quick}
              onChange={(e) => setQuick(e.target.checked)}
              disabled={triggering}
            />
            Quick (run inline, skip queue)
          </InlineLabel>
        </Field>

        {quick && !dryRun && (
          <Banner tone="warning">
            <AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            <strong>Quick + full build</strong> is not recommended — the
            request will almost certainly hit the ~230 s Functions HTTP
            timeout before the build finishes. Either enable <em>Dry run</em>{' '}
            for a smoke test, or leave <em>Quick</em> off to enqueue a real
            build.
          </Banner>
        )}

        <ButtonRow>
          <PrimaryButton onClick={doTrigger} disabled={triggering}>
            <PlayCircle size={16} />
            {triggering
              ? 'Triggering…'
              : dryRun
                ? quick ? 'Run dry-run inline' : 'Enqueue dry-run build'
                : quick ? 'Run inline build (not recommended)' : 'Enqueue full rebuild'}
          </PrimaryButton>
        </ButtonRow>

        {triggering && triggeredAt != null && (
          <RunningIndicator
            startedAt={triggeredAt}
            message={quick ? 'Running inline build…' : 'Submitting rebuild job…'}
          />
        )}

        {triggerError && <Banner tone="error">{triggerError}</Banner>}

        {trigger && !triggerError && (
          <>
            <Banner tone="success">
              <CheckCircle2 size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
              {trigger.jobId ? (
                <>
                  Job <code>{trigger.jobId}</code> {trigger.mode ?? 'queued'}.
                  The worker will write a new manifest when it finishes. This
                  page polls the manifest every 10 seconds.
                </>
              ) : (
                <>
                  Build {trigger.mode ?? 'completed'}.
                  {trigger.message ? ` ${trigger.message}` : ''}
                </>
              )}
            </Banner>
            <StatGrid>
              {trigger.jobId && <Metric label="Job ID" value={<code>{trigger.jobId}</code>} />}
              {trigger.mode && <Metric label="Mode" value={trigger.mode} />}
              {trigger.manifestKey && (
                <Metric label="Manifest key" value={<code>{trigger.manifestKey}</code>} />
              )}
            </StatGrid>
            {inlineManifest && (
              <>
                <CardDescription>
                  <strong>Inline manifest:</strong>
                </CardDescription>
                <RawJson data={inlineManifest} label="Inline manifest" />
              </>
            )}
            <RawJson data={trigger} label="Trigger response" />
          </>
        )}
      </Card>

      {/* ----------------------------- Latest manifest --------------------- */}
      <Card>
        <CardTitle>
          <FileSearch size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Latest build manifest
        </CardTitle>
        <CardDescription>
          The last <code>meta/manifest.json</code> written by the cache
          builder. Includes per-section counts, durations, and errors.
        </CardDescription>

        <ButtonRow>
          <SecondaryButton onClick={loadManifest} disabled={manifestLoading}>
            <RefreshCw size={16} />
            {manifestLoading ? 'Refreshing…' : 'Refresh manifest'}
          </SecondaryButton>
          {manifestRefreshedAt && (
            <span style={{ fontSize: 12, color: colors.text.tertiary }}>
              Refreshed {manifestRefreshedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>

        {manifestError && <Banner tone="error">{manifestError}</Banner>}

        {manifestMissing && !manifestError && (
          <Banner tone="warning">
            No manifest has been written yet — the cache has never been built
            in this environment. Trigger a build above to populate it.
          </Banner>
        )}

        {manifest && (
          <>
            <StatGrid>
              <Metric
                label="Started"
                value={fmtDate(manifest.startedAt)}
                hint={manifest.startedAt ? relativeTime(manifest.startedAt) : undefined}
              />
              <Metric
                label="Completed"
                value={fmtDate(manifest.completedAt)}
                hint={manifest.completedAt ? relativeTime(manifest.completedAt) : undefined}
              />
              <Metric label="Duration" value={fmtDuration(manifest.durationMs)} />
              <Metric
                label="Mode"
                value={manifest.dryRun ? 'Dry run' : (manifest.mode ?? 'full')}
                tone={manifest.dryRun ? 'neutral' : 'success'}
              />
              <Metric
                label="Errors"
                value={totalManifestErrors.toLocaleString()}
                tone={totalManifestErrors > 0 ? 'error' : 'success'}
              />
              {manifest.status && <Metric label="Status" value={manifest.status} />}
            </StatGrid>

            {sectionRows.length > 0 && (
              <SampleTable>
                <thead>
                  <tr>
                    <th>Section</th>
                    <th style={{ textAlign: 'right' }}>Count</th>
                    <th style={{ textAlign: 'right' }}>Duration</th>
                    <th style={{ textAlign: 'right' }}>Errors</th>
                    <th>State</th>
                  </tr>
                </thead>
                <tbody>
                  {sectionRows.map((s) => (
                    <tr key={s.key}>
                      <td>{s.label}</td>
                      <td style={{ textAlign: 'right' }}>
                        {s.count != null ? s.count.toLocaleString() : '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>{fmtDuration(s.durationMs)}</td>
                      <td
                        style={{
                          textAlign: 'right',
                          color: s.errorCount > 0 ? colors.error[600] : undefined,
                          fontWeight: s.errorCount > 0 ? typography.fontWeights.semibold : undefined,
                        }}
                      >
                        {s.errorCount.toLocaleString()}
                      </td>
                      <td>{s.skipped ? 'skipped' : 'ok'}</td>
                    </tr>
                  ))}
                </tbody>
              </SampleTable>
            )}

            {Array.isArray(manifest.errors) && manifest.errors.length > 0 && (
              <>
                <CardDescription style={{ marginTop: spacing[4] }}>
                  <strong>Top-level errors</strong>
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Section</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manifest.errors.map((err, i) => {
                      const obj = typeof err === 'string' ? { message: err } : err;
                      return (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{(obj as any).section ?? '—'}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                            {(obj as any).message}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </SampleTable>
              </>
            )}

            <RawJson data={manifest} label="Full manifest JSON" />
          </>
        )}
      </Card>

      {/* ----------------------------- App metadata ------------------------ */}
      <Card>
        <CardTitle>
          <Info size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          App metadata snapshot
        </CardTitle>
        <CardDescription>
          The <code>meta/app.json</code> blob served by{' '}
          <code>GET /api/getAppMetadata</code> — totals, top cities, top
          categories, the most recent event, and weekly add counts. Used by
          app landing pages and dashboards.
        </CardDescription>

        <ButtonRow>
          <SecondaryButton onClick={loadAppMeta} disabled={appMetaLoading}>
            <RefreshCw size={16} />
            {appMetaLoading ? 'Refreshing…' : 'Refresh metadata'}
          </SecondaryButton>
          <a
            href="/api/getAppMetadata"
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: typography.fontSizes.sm,
              color: colors.primary[600],
              textDecoration: 'none',
            }}
          >
            <ExternalLink size={14} />
            Open raw endpoint
          </a>
          {appMetaRefreshedAt && (
            <span style={{ fontSize: 12, color: colors.text.tertiary }}>
              Refreshed {appMetaRefreshedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>

        {appMetaError && <Banner tone="error">{appMetaError}</Banner>}

        {appMetaMissing && !appMetaError && (
          <Banner tone="warning">
            <AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            App metadata snapshot is not available yet. Trigger a cache build
            above to generate <code>meta/app.json</code>.
          </Banner>
        )}

        {appMeta && (
          <>
            <StatGrid>
              <Metric
                label="Refreshed"
                value={fmtDate(appMeta.builtAt)}
                hint={relativeTime(appMeta.builtAt)}
              />
              <Metric
                label="Upcoming"
                value={(appMeta.upcomingEvents?.total
                  ?? appMeta.totalUpcomingInPersonEvents + appMeta.totalUpcomingVirtualEvents
                ).toLocaleString()}
              />
              <Metric
                label="In person"
                value={(appMeta.upcomingEvents?.byFormat.inPerson
                  ?? appMeta.totalUpcomingInPersonEvents
                ).toLocaleString()}
              />
              <Metric
                label="Virtual only"
                value={(appMeta.upcomingEvents?.byFormat.virtual
                  ?? appMeta.totalUpcomingVirtualEvents
                ).toLocaleString()}
              />
              <Metric
                label="Hybrid"
                value={(appMeta.upcomingEvents?.byFormat.hybrid ?? 0).toLocaleString()}
              />
              <Metric
                label="Added past 7 days"
                value={appMeta.eventsAddedPastWeek.toLocaleString()}
              />
              {appMeta.upcomingCoverage && (
                <>
                  <Metric label="Organizations" value={appMeta.upcomingCoverage.organizations.toLocaleString()} />
                  <Metric label="Tech Companies" value={appMeta.upcomingCoverage.partners.toLocaleString()} />
                  <Metric label="Corporations" value={appMeta.upcomingCoverage.companies.toLocaleString()} />
                  <Metric label="Website sources" value={appMeta.upcomingCoverage.websiteSources.toLocaleString()} />
                  <Metric label="Cities" value={appMeta.upcomingCoverage.cities.toLocaleString()} />
                  <Metric label="Countries" value={appMeta.upcomingCoverage.countries.toLocaleString()} />
                  <Metric label="Continents" value={appMeta.upcomingCoverage.continents.toLocaleString()} />
                </>
              )}
            </StatGrid>

            {Array.isArray(appMeta.topCitiesByEventCount) && appMeta.topCitiesByEventCount.length > 0 && (
              <>
                <CardDescription>
                  <strong>Top cities</strong> (showing up to 10)
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>City</th>
                      <th>State</th>
                      <th>Country</th>
                      <th style={{ textAlign: 'right' }}>Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appMeta.topCitiesByEventCount.slice(0, 10).map((c, i) => (
                      <tr key={i}>
                        <td>{c.city ?? '—'}</td>
                        <td>{c.state ?? '—'}</td>
                        <td>{c.country ?? '—'}</td>
                        <td style={{ textAlign: 'right' }}>
                          {c.eventCount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            {Array.isArray(appMeta.topCategoriesByEventCount) && appMeta.topCategoriesByEventCount.length > 0 && (
              <>
                <CardDescription style={{ marginTop: spacing[4] }}>
                  <strong>Top categories</strong> (showing up to 10)
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th style={{ textAlign: 'right' }}>Events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appMeta.topCategoriesByEventCount.slice(0, 10).map((c, i) => (
                      <tr key={i}>
                        <td>{c.categoryName}</td>
                        <td style={{ textAlign: 'right' }}>
                          {c.eventCount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            {appMeta.mostRecentEventAdded && (
              <CardDescription style={{ marginTop: spacing[4] }}>
                <strong>Most recent event:</strong>{' '}
                {appMeta.mostRecentEventAdded.title
                  ?? `#${appMeta.mostRecentEventAdded.eventId}`}
                {appMeta.mostRecentEventAdded.startDate
                  ? ` — starts ${formatEventLocalDateTime(appMeta.mostRecentEventAdded.startDate)}`
                  : ''}
                {appMeta.mostRecentEventAdded.dateCreated
                  ? ` (added ${relativeTime(appMeta.mostRecentEventAdded.dateCreated)})`
                  : ''}
              </CardDescription>
            )}

            <RawJson data={appMeta} label="Full app metadata JSON" />
          </>
        )}
      </Card>

      {/* ----------------------------- Cache stats ------------------------- */}
      <Card>
        <CardTitle>
          <BarChart3 size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Cache hit/miss reporting
        </CardTitle>
        <CardDescription>
          Telemetry from <code>GET /api/getCacheStats</code> — pulled from
          App Insights / KQL. Use the filters below to scope by gate, time
          window, and grouping fields. Examples: <code>since=P7D</code>,{' '}
          <code>gate=city</code>, <code>groupBy=city,state,iso2</code>.
        </CardDescription>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: spacing[4],
          }}
        >
          <Field>
            <Label htmlFor="statsSince">Since (ISO 8601 duration)</Label>
            <Input
              id="statsSince"
              type="text"
              placeholder="P1D"
              value={statsSince}
              onChange={(e) => setStatsSince(e.target.value)}
              disabled={statsLoading}
            />
            <div style={{ fontSize: 12, color: colors.text.tertiary, marginTop: 4 }}>
              e.g. <code>PT1H</code>, <code>P1D</code>, <code>P7D</code>
            </div>
          </Field>
          <Field>
            <Label htmlFor="statsGate">Gate</Label>
            <Select
              id="statsGate"
              value={statsGate}
              onChange={(e) => setStatsGate(e.target.value as '' | CacheStatsGate)}
              disabled={statsLoading}
            >
              <option value="">All gates</option>
              <option value="city">city</option>
              <option value="categoryVirtual">categoryVirtual</option>
              <option value="entity">entity</option>
            </Select>
          </Field>
          <Field>
            <Label htmlFor="statsGroupBy">Group by (comma-separated)</Label>
            <Input
              id="statsGroupBy"
              type="text"
              placeholder="reason"
              value={statsGroupBy}
              onChange={(e) => setStatsGroupBy(e.target.value)}
              disabled={statsLoading}
            />
            <div style={{ fontSize: 12, color: colors.text.tertiary, marginTop: 4 }}>
              e.g. <code>reason</code>, <code>city,state,iso2</code>,{' '}
              <code>categoryName</code>, <code>entityId</code>
            </div>
          </Field>
          <Field>
            <Label htmlFor="statsTop">Top N rows</Label>
            <Input
              id="statsTop"
              type="number"
              min={1}
              placeholder=""
              value={statsTop}
              onChange={(e) => setStatsTop(e.target.value)}
              disabled={statsLoading}
            />
          </Field>
        </div>

        <Field>
          <InlineLabel>
            <input
              type="checkbox"
              checked={statsRaw}
              onChange={(e) => setStatsRaw(e.target.checked)}
              disabled={statsLoading}
            />
            Return raw KQL columns and rows
          </InlineLabel>
        </Field>

        <ButtonRow>
          <PrimaryButton onClick={loadStats} disabled={statsLoading}>
            <BarChart3 size={16} />
            {statsLoading ? 'Querying…' : 'Run query'}
          </PrimaryButton>
          <SecondaryButton
            onClick={() => {
              setStatsSince('P1D');
              setStatsGate('');
              setStatsGroupBy('');
              setStatsTop('');
              setStatsRaw(false);
            }}
            disabled={statsLoading}
          >
            Reset
          </SecondaryButton>
          {statsRefreshedAt && (
            <span style={{ fontSize: 12, color: colors.text.tertiary }}>
              Refreshed {statsRefreshedAt.toLocaleTimeString()}
            </span>
          )}
        </ButtonRow>

        {statsError && <Banner tone="error">{statsError}</Banner>}

        {statsMissing && !statsError && (
          <Banner tone="warning">
            <AlertTriangle size={16} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
            <code>/api/getCacheStats</code> is not deployed yet. This panel
            will activate once the backend exposes it.
          </Banner>
        )}

        {stats && (
          <>
            {(stats.window?.startTime || stats.window?.endTime || stats.since) && (
              <CardDescription style={{ marginTop: spacing[4] }}>
                <strong>Window:</strong>{' '}
                {stats.window?.startTime ? fmtDate(stats.window.startTime) : '—'}
                {' → '}
                {stats.window?.endTime ? fmtDate(stats.window.endTime) : 'now'}
                {stats.since && <> · since <code>{stats.since}</code></>}
                {stats.gate && <> · gate <code>{stats.gate}</code></>}
              </CardDescription>
            )}

            {stats.summary && (
              <StatGrid>
                {typeof stats.summary.total === 'number' && (
                  <Metric label="Total" value={stats.summary.total.toLocaleString()} />
                )}
                {typeof stats.summary.hits === 'number' && (
                  <Metric
                    label="Hits"
                    value={stats.summary.hits.toLocaleString()}
                    tone="success"
                  />
                )}
                {typeof stats.summary.misses === 'number' && (
                  <Metric
                    label="Misses"
                    value={stats.summary.misses.toLocaleString()}
                    tone={stats.summary.misses > 0 ? 'warning' : 'neutral'}
                  />
                )}
                {typeof stats.summary.hitRate === 'number' && (
                  <Metric
                    label="Hit rate"
                    value={`${(stats.summary.hitRate * 100).toFixed(1)}%`}
                    tone={
                      stats.summary.hitRate >= 0.8
                        ? 'success'
                        : stats.summary.hitRate >= 0.5
                          ? 'neutral'
                          : 'warning'
                    }
                  />
                )}
              </StatGrid>
            )}

            {Array.isArray(stats.summary?.byGate) && stats.summary!.byGate!.length > 0 && (
              <>
                <CardDescription style={{ marginTop: spacing[4] }}>
                  <strong>By gate</strong>
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      <th>Gate</th>
                      <th style={{ textAlign: 'right' }}>Total</th>
                      <th style={{ textAlign: 'right' }}>Hits</th>
                      <th style={{ textAlign: 'right' }}>Misses</th>
                      <th style={{ textAlign: 'right' }}>Hit rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.summary!.byGate!.map((g, i) => (
                      <tr key={i}>
                        <td><code>{g.gate}</code></td>
                        <td style={{ textAlign: 'right' }}>
                          {(g.total ?? 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {(g.hits ?? 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {(g.misses ?? 0).toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {typeof g.hitRate === 'number'
                            ? `${(g.hitRate * 100).toFixed(1)}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            {Array.isArray(stats.rows) && stats.rows.length > 0 && (
              <>
                <CardDescription style={{ marginTop: spacing[4] }}>
                  <strong>Grouped rows</strong> ({stats.rows.length})
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      {Object.keys(stats.rows[0]).map((k) => (
                        <th
                          key={k}
                          style={{
                            textAlign:
                              typeof (stats.rows![0] as any)[k] === 'number'
                                ? 'right'
                                : 'left',
                          }}
                        >
                          {k}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {stats.rows.map((row, i) => (
                      <tr key={i}>
                        {Object.keys(stats.rows![0]).map((k) => {
                          const v = (row as any)[k];
                          const numeric = typeof v === 'number';
                          return (
                            <td
                              key={k}
                              style={{ textAlign: numeric ? 'right' : 'left' }}
                            >
                              {v == null
                                ? '—'
                                : numeric
                                  ? v.toLocaleString()
                                  : typeof v === 'boolean'
                                    ? v ? 'true' : 'false'
                                    : String(v)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            {Array.isArray(stats.columns) && Array.isArray(stats.rawRows) && (
              <>
                <CardDescription style={{ marginTop: spacing[4] }}>
                  <strong>Raw KQL result</strong> ({stats.rawRows.length} rows)
                </CardDescription>
                <SampleTable>
                  <thead>
                    <tr>
                      {stats.columns.map((c) => <th key={c}>{c}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {stats.rawRows.map((row, i) => (
                      <tr key={i}>
                        {(row as unknown[]).map((cell, j) => (
                          <td key={j}>
                            {cell == null
                              ? '—'
                              : typeof cell === 'object'
                                ? JSON.stringify(cell)
                                : String(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </SampleTable>
              </>
            )}

            <RawJson data={stats} label="Full cache stats response" />
          </>
        )}
      </Card>

      <LastRunCard record={lastRun} />
    </AdminLayout>
  );
};

export default AdminCachePage;
